import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/nav/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck, Lock, Moon, Sprout, Wallet as WalletIcon, ArrowDownLeft, ArrowUpRight, TrendingUp, Coins, ScanFace } from "lucide-react";
import { loginWithPasskey, passkeyError, passkeysSupported } from "@/lib/passkeys";

// The demo-credentials hint is for the preview sandbox only — never on a deployed host.
const IS_PREVIEW_HOST = typeof window !== "undefined" && (
  window.location.hostname.includes(".preview.") || window.location.hostname === "localhost");

const POSITIONS = [
  { sym: "AAPL", name: "Apple", price: "228.52", chg: "+1.24%", up: true, badge: "AA" },
  { sym: "NVDA", name: "NVIDIA", price: "121.40", chg: "+2.86%", up: true, badge: "NV" },
  { sym: "2222.SR", name: "Saudi Aramco", price: "28.65", chg: "+0.31%", up: true, badge: "AR" },
];

const COUNTRIES = [
  "Qatar", "Saudi Arabia", "United Arab Emirates", "Kuwait", "Bahrain", "Oman",
  "United States", "United Kingdom", "Eurozone", "Japan", "Switzerland",
  "Canada", "Australia", "India", "Pakistan", "Egypt", "Turkey", "Singapore", "China",
];
const COUNTRY_CCY = {
  "Qatar": "QAR", "Saudi Arabia": "SAR", "United Arab Emirates": "AED", "Kuwait": "KWD",
  "Bahrain": "BHD", "Oman": "OMR", "United States": "USD", "United Kingdom": "GBP",
  "Eurozone": "EUR", "Japan": "JPY", "Switzerland": "CHF", "Canada": "CAD",
  "Australia": "AUD", "India": "INR", "Pakistan": "PKR", "Egypt": "EGP",
  "Turkey": "TRY", "Singapore": "SGD", "China": "CNY",
};

const COPY = {
  en: {
    h1a: "One account for the world's markets — ", h1b: "built for everyone.",
    sub: "Global & GCC stocks, Shariah-compliant portfolios, USD high-yield savings and multi-currency accounts — in one secure platform.",
    chips: ["Bank-grade security", "Islamic investing", "Multi-currency wallet", "4.85% USD APY"],
    balance: "Total Balance", positions: "Your positions", seeAll: "See all",
    accounts: "Accounts", deposit: "Deposit", invest: "Invest", today: "today",
    welcome: "Welcome back", create: "Create your account",
    welcomeSub: "Sign in to your Tharwy account", createSub: "Start investing in minutes",
    demo: "Demo access", newTo: "New to Tharwy?", signupEmail: "Sign up with email",
    forgot: "Forgotten password?", faceId: "Sign in with Face ID",
    faceWait: "Waiting for your device...",
    faceHint: "Works once you've enabled it in Profile → Security on this device.",
    orLogin: "Or continue with", orRegister: "Or sign up with",
    ssl: "256-bit SSL encryption.",
    sslBody: "Our website and app are secured with 256-bit encryption to keep your information safe and backed up continuously.",
    risk: "Risk warning: Investing involves risk, including possible loss of capital. Tharwy is a demonstration platform. QFC regulatory disclosures shown for illustration.",
  },
  ar: {
    h1a: "حساب واحد لأسواق العالم — ", h1b: "للجميع.",
    sub: "أسهم عالمية وخليجية، ومحافظ متوافقة مع الشريعة، وتوفير بالدولار بعائد مرتفع، وحسابات متعددة العملات — في منصة واحدة آمنة.",
    chips: ["أمان بمستوى البنوك", "استثمار إسلامي", "محفظة متعددة العملات", "عائد ٤.٨٥٪ بالدولار"],
    balance: "الرصيد الإجمالي", positions: "مراكزك", seeAll: "عرض الكل",
    accounts: "الحسابات", deposit: "إيداع", invest: "استثمار", today: "اليوم",
    welcome: "مرحباً بعودتك", create: "أنشئ حسابك",
    welcomeSub: "سجّل الدخول إلى حسابك في ثروي", createSub: "ابدأ الاستثمار في دقائق",
    demo: "حساب تجريبي", newTo: "جديد على ثروي؟", signupEmail: "أنشئ حساباً بالبريد",
    forgot: "نسيت كلمة المرور؟", faceId: "الدخول ببصمة الوجه",
    faceWait: "بانتظار جهازك...",
    faceHint: "يعمل بعد تمكينه من الملف الشخصي ← الأمان على هذا الجهاز.",
    orLogin: "أو تابع بواسطة", orRegister: "أو أنشئ حساباً بواسطة",
    ssl: "تشفير SSL بـ ٢٥٦ بت.",
    sslBody: "موقعنا وتطبيقنا محميان بتشفير ٢٥٦ بت لحفظ بياناتك بأمان مع نسخ احتياطي مستمر.",
    risk: "تنبيه المخاطر: الاستثمار يحمل مخاطر تشمل احتمال خسارة رأس المال. ثروي منصة عرض توضيحي، والإفصاحات التنظيمية معروضة للتوضيح فقط.",
  },
};

export default function Login() {
  const { login, register, socialDemo, setUser, t, lang } = useApp();
  const c = COPY[lang === "ar" ? "ar" : "en"];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get("ref") || "").toUpperCase();
  const [tab, setTab] = useState(refFromUrl ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("Qatar");
  const [referralCode, setReferralCode] = useState(refFromUrl);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = passkeysSupported();

  const faceIdLogin = async () => {
    setPasskeyBusy(true);
    try {
      const u = await loginWithPasskey();
      setUser(u);
      toast.success("Welcome back", { description: "Signed in with Face ID" });
      navigate(u?.onboarding_completed === false ? "/onboarding" : "/");
    } catch (err) {
      toast.error("Face ID sign-in failed", { description: passkeyError(err) });
    } finally { setPasskeyBusy(false); }
  };

  const socialLogin = async (provider) => {
    if (provider === "Google") {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    // Outlook (Microsoft) & Apple — simulated demo sign-in in the sandbox.
    const key = provider === "Microsoft" ? "microsoft" : "apple";
    setLoading(true);
    try {
      await socialDemo(key);
      toast.success(`Signed in with ${provider === "Microsoft" ? "Outlook" : "Apple"} (demo)`);
      navigate("/");
    } catch (err) {
      toast.error("Sign-in failed", { description: err?.response?.data?.detail || "Please try again" });
    } finally { setLoading(false); }
  };

  const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
  );
  const MicrosoftIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#F25022" d="M3 3h8.5v8.5H3z"/><path fill="#7FBA00" d="M12.5 3H21v8.5h-8.5z"/><path fill="#00A4EF" d="M3 12.5h8.5V21H3z"/><path fill="#FFB900" d="M12.5 12.5H21V21h-8.5z"/></svg>
  );
  const AppleIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-foreground" aria-hidden="true"><path d="M16.37 12.63c-.02-2.06 1.68-3.05 1.76-3.1-0.96-1.4-2.45-1.6-2.98-1.62-1.27-.13-2.48.75-3.12.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.03 8.23.69.99 1.5 2.1 2.57 2.06 1.03-.04 1.42-.67 2.67-.67 1.24 0 1.6.67 2.69.65 1.11-.02 1.81-1.01 2.49-2.01.78-1.15 1.1-2.27 1.12-2.33-.02-.01-2.15-.83-2.18-3.27M14.5 6.2c.57-.69.95-1.65.85-2.6-.82.03-1.81.54-2.4 1.23-.53.61-.99 1.59-.86 2.52.91.07 1.84-.46 2.41-1.15"/></svg>
  );

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") { await login(email, password, mfaRequired ? mfaCode : undefined); navigate("/"); }
      else { const u = await register({ name, email, password, country, base_currency: COUNTRY_CCY[country], referral_code: referralCode || undefined }); navigate(u?.onboarding_completed === false ? "/onboarding" : "/"); }
      toast.success("Welcome to Tharwy");
      return;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (detail === "MFA_REQUIRED") {
        setMfaRequired(true);
        toast.info("Two-factor required", { description: "Enter the 6-digit code from your authenticator app" });
      } else if (tab === "register" && /already registered/i.test(detail || "")) {
        setTab("login");
        toast.error("That email already has an account", {
          description: "Switched you to Log in — use your password, or reset it if you've forgotten." });
      } else if (status === 429) {
        toast.error("Too many attempts", {
          description: detail || "Please wait a few minutes, or reset your password to unlock now." });
      } else {
        toast.error(tab === "login" ? "Couldn't sign you in" : "Couldn't create your account", {
          description: detail || "Please check your details and try again." });
      }
    } finally { setLoading(false); }
  };

  const BrandMark = ({ tone = "light" }) => (
    <div className="flex items-center" data-testid="login-brand">
      <Logo className="h-20 w-auto" light={tone === "light"} />
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[hsl(var(--background))]">
      {/* Left brand panel — Fynix green */}
      <div className="relative hidden lg:flex lg:col-span-3 flex-col justify-center p-10 xl:p-14 navy-strip text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.22] pointer-events-none" aria-hidden="true"
          style={{ backgroundImage: "url(/night-sky.jpg)", backgroundSize: "cover", backgroundPosition: "bottom" }} />
        <div className="absolute inset-0 hero-sheen opacity-60" />
        <div className="relative z-10 w-full grid xl:grid-cols-2 gap-10 xl:gap-14 items-center">
          {/* Text column */}
          <div>
            <BrandMark tone="light" />
            <h1 className="mt-10 text-3xl xl:text-[2.6rem] font-bold leading-[1.08] tracking-tight">
              {c.h1a}<span className="text-[color:var(--tharwy-lime)]">{c.h1b}</span>
            </h1>
            <p className="mt-4 text-white/70 leading-relaxed text-sm max-w-md">
              {c.sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {[ShieldCheck, Sprout, WalletIcon, TrendingUp].map((Icon, i) => [Icon, c.chips[i]]).map(([Icon,label])=>(
                <div key={label} className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm">
                  <Icon className="h-4 w-4 text-[color:var(--tharwy-lime)]" /> {label}
                </div>
              ))}
            </div>
          </div>

          {/* Floating dashboard preview — echoes the marketing front page */}
          <div className="relative">
            <div className="balance-hero rounded-2xl p-5 text-[#332654] shadow-[0_30px_70px_-38px_rgba(0,0,0,0.6)]">
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#332654]/70">{c.balance}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold"><span className="h-2 w-2 rounded-full bg-[#332654] live-dot" /> LIVE</span>
                </div>
                <div className="mt-1.5 numeric text-3xl font-bold">$180,650.90</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold"><TrendingUp className="h-3.5 w-3.5" /> +$4,212.65 · +2.4% {c.today}</div>
                <div className="mt-4 flex gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[#3E2E65]"><ArrowDownLeft className="h-3.5 w-3.5" /> {c.deposit}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#3E2E65] px-3 py-1.5 text-xs font-semibold text-white"><ArrowUpRight className="h-3.5 w-3.5" /> {c.invest}</span>
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-2xl bg-white p-3.5 text-[#1A1330] shadow-[0_26px_60px_-40px_rgba(0,0,0,0.55)]">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs font-semibold">{c.positions}</span>
                <span className="text-[11px] font-medium text-[color:var(--tharwy-lime-700)]">{c.seeAll}</span>
              </div>
              <div className="space-y-1.5">
                {POSITIONS.map((p) => (
                  <div key={p.sym} className="flex items-center gap-2.5">
                    <div className="h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                      style={{ background: p.up ? "var(--tharwy-lime)" : "#94A3B8" }}>{p.badge}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-semibold leading-none">{p.name}</div>
                      <div className="text-[9px] text-slate-400 mt-0.5">{p.sym}</div>
                    </div>
                    <div className="text-right">
                      <div className="numeric text-[12px] font-semibold">${p.price}</div>
                      <div className={`numeric text-[9px] font-medium inline-flex items-center gap-0.5 ${p.up ? "text-emerald-600" : "text-red-500"}`}>
                        {p.up ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownLeft className="h-2.5 w-2.5" />}{p.chg}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 rounded-2xl bg-white px-3 py-2.5 w-max shadow-[0_24px_60px_-34px_rgba(0,0,0,0.5)]">
              <div className="h-8 w-8 rounded-xl bg-[#EFF6D8] flex items-center justify-center text-[#3E2E65]"><Coins className="h-4 w-4" /></div>
              <div className="text-[#1A1330]">
                <div className="text-[10px] text-slate-400 leading-none">{c.accounts}</div>
                <div className="text-[12px] font-semibold">USD · QAR · AED · EUR</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-2 flex items-center justify-center px-4 py-10 sm:px-6">
        <Card className="w-full max-w-md rounded-2xl bg-card p-6 sm:p-8 shadow-premium border-0 animate-fade-up login-card">
          <div className="lg:hidden mb-6"><BrandMark tone="dark" /></div>

          <div className="mb-6">
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">
              {tab === "login" ? c.welcome : c.create}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {tab === "login" ? c.welcomeSub : c.createSub}
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">{t("login")}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t("register")}</TabsTrigger>
            </TabsList>
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <TabsContent value="register" className="m-0 space-y-4">
                <div className="space-y-1.5">
                  <Label>Full name</Label>
                  <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Ahmed Al-Thani" required={tab==="register"} data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Country of residence</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger data-testid="register-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((c) => (
                        <SelectItem key={c} value={c}>{c} · {COUNTRY_CCY[c]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground" data-testid="register-base-currency-note">
                    We'll open your account in your local currency (<span className="font-medium text-foreground">{COUNTRY_CCY[country]}</span>) plus USD.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Referral code <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input value={referralCode} onChange={(e)=>setReferralCode(e.target.value.toUpperCase())}
                    placeholder="MX7K2QP4" className="numeric tracking-widest" data-testid="register-referral-input" />
                  {referralCode && (
                    <p className="text-[11px] text-[color:var(--tharwy-lime-700)]" data-testid="register-referral-note">
                      You and your friend each get $25 once you fund $100.
                    </p>
                  )}
                </div>
              </TabsContent>
              <div className="space-y-1.5">
                <Label>{t("email")}</Label>
                <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("password")}</Label>
                  {tab === "login" && (
                    <Link to="/forgot-password"
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="login-forgot-password-link">
                      {c.forgot}
                    </Link>
                  )}
                </div>
                <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password-input" />
              </div>
              {tab==="login" && mfaRequired && (
                <div className="space-y-1.5" data-testid="login-mfa-section">
                  <Label>Authentication code</Label>
                  <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={mfaCode}
                    onChange={(e)=>setMfaCode(e.target.value.replace(/\D/g,""))} placeholder="123456"
                    className="tracking-[0.5em] text-center numeric" data-testid="login-mfa-input" autoFocus />
                  <p className="text-[11px] text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
                </div>
              )}
              <Button type="submit" className="w-full rounded-xl" disabled={loading}
                data-testid={tab==="login" ? "login-submit-button" : "register-submit-button"}>
                {loading ? "Please wait..." : tab==="login" ? (mfaRequired ? "Verify & sign in" : t("login")) : t("register")}
              </Button>
            </form>
          </Tabs>

          {tab === "login" && passkeySupported && (
            <div className="mt-3">
              <Button type="button" variant="outline" className="w-full rounded-xl gap-2"
                onClick={faceIdLogin} disabled={passkeyBusy} data-testid="login-faceid-button">
                <ScanFace className="h-4 w-4" />
                {passkeyBusy ? c.faceWait : c.faceId}
              </Button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground" data-testid="login-faceid-hint">
                {c.faceHint}
              </p>
            </div>
          )}

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-[11px] uppercase tracking-wide">
              <span className="bg-card px-3 text-muted-foreground">
                {tab === "login" ? c.orLogin : c.orRegister}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="social-login-row">
            <Button type="button" variant="outline" className="gap-2 rounded-xl" onClick={() => socialLogin("Google")} data-testid="social-google-button"><GoogleIcon /> Google</Button>
            <Button type="button" variant="outline" className="gap-2 rounded-xl" onClick={() => socialLogin("Microsoft")} data-testid="social-microsoft-button"><MicrosoftIcon /> Microsoft</Button>
            <Button type="button" variant="outline" className="gap-2 rounded-xl" onClick={() => socialLogin("Apple")} data-testid="social-apple-button"><AppleIcon /> Apple</Button>
          </div>
          {tab === "login" && (
            <button type="button" onClick={() => setTab("register")} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="signup-email-link">
              {c.newTo} <span className="font-medium text-foreground underline underline-offset-2">{c.signupEmail}</span>
            </button>
          )}

          {IS_PREVIEW_HOST && (
            <div className="mt-6 rounded-xl bg-secondary p-3 text-xs text-muted-foreground"
              data-testid="login-demo-hint">
              <div className="font-medium text-foreground mb-1">{c.demo}</div>
              User: demo@tharwy.com / Demo@12345<br/>Admin: admin@tharwy.com / Admin@12345<br/>
              Brand new (walks onboarding): newuser@tharwy.com / New@12345
            </div>
          )}
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#EFF6D8] bg-[#F7FAED] p-3 text-xs text-[#3E2E65]" data-testid="login-ssl-note">
            <Lock className="h-4 w-4 mt-0.5 shrink-0" />
            <span><span className="font-semibold">{c.ssl}</span> {c.sslBody}</span>
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground leading-relaxed">
            {c.risk}
          </p>
        </Card>
      </div>
    </div>
  );
}
